/**
 * PeerPrep Medication Classification Hooks
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import type {
  DrugCard,
  MedicationInputState,
  MedicationClass,
  AIClassificationResponse,
} from "../types/medication";
import {
  createMedicationMatcher,
  CONFIDENCE_THRESHOLD,
  type MedicationMatcher,
} from "../lib/medicationMatcher";

// ============================================
// Supabase Client (Vite)
// ============================================

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// ============================================
// useMedicationCache Hook
// ============================================

interface MedicationCacheState {
  medications: DrugCard[];
  classes: MedicationClass[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export function useMedicationCache() {
  const [state, setState] = useState<MedicationCacheState>({
    medications: [],
    classes: [],
    isLoading: true,
    error: null,
    lastUpdated: null,
  });

  const loadCache = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const { data: medsData, error: medsError } = await supabase
        .from("medications_with_class")
        .select("*");

      if (medsError) throw medsError;

      const { data: classesData, error: classesError } = await supabase
        .from("medication_classes")
        .select("*");

      if (classesError) throw classesError;

      const medications: DrugCard[] = (medsData || []).map((row) => ({
        id: row.id,
        generic_name: row.generic_name,
        brand_names: row.brand_names || [],
        synonyms: row.synonyms || [],
        class_id: row.class_id,
        class_name: row.class_name,
        dea_schedule: row.dea_schedule,
        indications: row.indications || [],
        routes: row.routes || [],
        high_prior_auth_burden: row.high_prior_auth_burden,
        common_denial_reasons: row.common_denial_reasons,
        source: row.source,
        confidence: row.confidence,
        needs_review: row.needs_review,
        created_at: row.created_at,
        updated_at: row.updated_at,
      }));

      const classes: MedicationClass[] = (classesData || []).map((row) => ({
        class_id: row.class_id,
        class_name: row.class_name,
        description: row.description,
        parent_class_id: row.parent_class_id,
        keywords: row.keywords || [],
        rebuttal_strategies: row.rebuttal_strategies,
      }));

      setState({
        medications,
        classes,
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to load medications",
      }));
    }
  }, []);

  useEffect(() => {
    loadCache();
  }, [loadCache]);

  const addToCache = useCallback((drug: DrugCard) => {
    setState((prev) => ({
      ...prev,
      medications: [...prev.medications, drug],
    }));
  }, []);

  return {
    ...state,
    refresh: loadCache,
    addToCache,
  };
}

// ============================================
// useMedicationInput Hook
// ============================================

interface UseMedicationInputOptions {
  debounceMs?: number;
  autoClassify?: boolean;
  context?: {
    specialty?: string;
    indication?: string;
  };
}

export function useMedicationInput(
  cache: ReturnType<typeof useMedicationCache>,
  options: UseMedicationInputOptions = {}
) {
  const { debounceMs = 300, autoClassify = true, context } = options;

  const [state, setState] = useState<MedicationInputState>({
    input: "",
    status: "idle",
    resolved_drug: null,
    confidence: 0,
    override_open: false,
    error: undefined,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const matcher = useMemo<MedicationMatcher | null>(() => {
    if (cache.medications.length === 0) return null;
    return createMedicationMatcher(cache.medications);
  }, [cache.medications]);

  const classifyWithAI = useCallback(
    async (input: string): Promise<AIClassificationResponse> => {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/classify-medication`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            medication_input: input,
            context,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Classification failed: ${response.status}`);
      }

      return response.json();
    },
    [context]
  );

  const processInput = useCallback(
    async (input: string) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      if (!input.trim()) {
        setState({
          input,
          status: "idle",
          resolved_drug: null,
          confidence: 0,
          override_open: false,
        });
        return;
      }

      setState((prev) => ({ ...prev, input, status: "matching" }));

      if (matcher) {
        const result = matcher.match(input);

        if (result.drug && result.confidence >= CONFIDENCE_THRESHOLD) {
          setState({
            input,
            status: "success",
            resolved_drug: result.drug,
            confidence: result.confidence,
            override_open: false,
          });
          return;
        }

        if (result.needs_ai_fallback && autoClassify) {
          setState((prev) => ({ ...prev, status: "classifying" }));

          try {
            const aiResult = await classifyWithAI(input);

            if (aiResult.success && aiResult.drug_card) {
              cache.addToCache(aiResult.drug_card);

              setState({
                input,
                status: "success",
                resolved_drug: aiResult.drug_card,
                confidence: aiResult.drug_card.confidence,
                override_open: aiResult.drug_card.needs_review,
              });
              return;
            }

            setState({
              input,
              status: "error",
              resolved_drug: result.drug,
              confidence: result.confidence,
              override_open: true,
              error: aiResult.error || "Classification failed",
            });
          } catch (error) {
            setState({
              input,
              status: "error",
              resolved_drug: result.drug,
              confidence: result.confidence,
              override_open: true,
              error:
                error instanceof Error
                  ? error.message
                  : "Classification failed",
            });
          }
          return;
        }

        setState({
          input,
          status: result.drug ? "success" : "idle",
          resolved_drug: result.drug,
          confidence: result.confidence,
          override_open:
            !result.drug || result.confidence < CONFIDENCE_THRESHOLD,
        });
      }
    },
    [matcher, autoClassify, classifyWithAI, cache]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setState((prev) => ({ ...prev, input: value }));

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        processInput(value);
      }, debounceMs);
    },
    [processInput, debounceMs]
  );

  const triggerClassification = useCallback(() => {
    if (state.input.trim()) {
      processInput(state.input);
    }
  }, [state.input, processInput]);

  const applyOverride = useCallback(
    async (classId: string, reason?: string) => {
      if (!state.resolved_drug) return;

      const selectedClass = cache.classes.find((c) => c.class_id === classId);
      if (!selectedClass) return;

      try {
        await supabase.from("medication_overrides").insert({
          medication_id: state.resolved_drug.id,
          original_class_id: state.resolved_drug.class_id,
          override_class_id: classId,
          reason,
          overridden_by: "current_user", // Replace with Clerk user ID
        });

        const updatedDrug: DrugCard = {
          ...state.resolved_drug,
          class_id: classId,
          class_name: selectedClass.class_name,
          source: "manual_override",
          confidence: 1.0,
          needs_review: false,
        };

        setState((prev) => ({
          ...prev,
          resolved_drug: updatedDrug,
          confidence: 1.0,
          override_open: false,
        }));

        cache.addToCache(updatedDrug);
      } catch (error) {
        console.error("Failed to save override:", error);
      }
    },
    [state.resolved_drug, cache]
  );

  const toggleOverride = useCallback((open?: boolean) => {
    setState((prev) => ({
      ...prev,
      override_open: open ?? !prev.override_open,
    }));
  }, []);

  const clear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setState({
      input: "",
      status: "idle",
      resolved_drug: null,
      confidence: 0,
      override_open: false,
    });
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return {
    ...state,
    handleInputChange,
    triggerClassification,
    applyOverride,
    toggleOverride,
    clear,
    isLoading: state.status === "matching" || state.status === "classifying",
    isClassifying: state.status === "classifying",
  };
}

// ============================================
// useRebuttalStrategies Hook
// ============================================

export function useRebuttalStrategies(
  classId: string | null,
  cache: ReturnType<typeof useMedicationCache>
) {
  return useMemo(() => {
    if (!classId) return [];
    const medicationClass = cache.classes.find((c) => c.class_id === classId);
    return medicationClass?.rebuttal_strategies || [];
  }, [classId, cache.classes]);
}
